import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useKpis } from '../contexts/kpis';
import KpiFilters from '../forms/filters';
import ProductionCards from './production';
import FunnelChart from './funnel';
import RatesGrid from './rates';
import OocytesChart from './oocytes-chart';
import FrequencyChart from './frequency-chart';

import './styles/dashboard.css';

// Panel de informes general (HOME). Una sola query a GET /admin/kpis con
// view=full (producción + embudo + tasas; el bloque `pending` viene pero no se
// renderiza todavía). Filtrable por cliente / finca / rango de fechas.

const todayStr = () => new Date().toLocaleDateString('en-CA');
const yearStart = () => `${new Date().getFullYear()}-01-01`;

export default function KpiDashboard() {
    // Filtros en la URL (compartibles / sobreviven al volver de un detalle).
    // `from`/`to` ausentes ⇒ rango por defecto (inicio de año → hoy); `from=` vacío
    // explícito se respeta (el usuario borró la fecha).
    const [params, setParams] = useSearchParams();
    const filters = useMemo(() => ({
        client_id: params.get('client_id') || '',
        farm_id: params.get('farm_id') || '',
        from: params.get('from') ?? yearStart(),
        to: params.get('to') ?? todayStr(),
    }), [params]);
    const setFilter = (patch) => {
        const next = new URLSearchParams(params);
        Object.entries(patch).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else if (key === 'from' || key === 'to') next.set(key, '');
            else next.delete(key);
        });
        setParams(next, { replace: true });
    };

    const { data, isLoading, isFetching, error, refetch } = useKpis('full', filters);

    return (
        <div className={`report-panel ${isFetching ? 'report-panel--busy' : ''}`}>
            <header className="report-panel__header">
                <h1 className="report-panel__title">Informes</h1>
                <p className="report-panel__subtitle">Indicadores reproductivos del hato.</p>
            </header>

            <KpiFilters value={filters} onChange={setFilter} onRefresh={() => refetch()} busy={isFetching} />

            {error && (
                <div className="report-error">
                    {error.status === 403 ? 'No tenés permiso para ver los informes.' : (error.message || 'No se pudieron cargar los informes.')}
                </div>
            )}

            {isLoading && !data ? (
                <div className="report-state">Cargando informes…</div>
            ) : (
                <>
                    <div className="report-grid">
                        <div className="report-grid__main">
                            <section className="report-section">
                                <h2 className="report-section__title">Producción global</h2>
                                <ProductionCards production={data?.production} />
                            </section>
                            <RatesGrid rates={data?.rates} />
                        </div>
                        <div className="report-grid__side">
                            <FunnelChart funnel={data?.funnel} />
                        </div>
                    </div>

                    <section className="report-trends">
                        <OocytesChart data={data?.trends?.oocytes_per_session} />
                        <FrequencyChart series={data?.trends?.aspiration_frequency} label="Frecuencia de aspiraciones (OPU)" tone="accent" />
                        <FrequencyChart series={data?.trends?.transfer_frequency} label="Frecuencia de transferencias (TE)" tone="primary" />
                    </section>
                </>
            )}
        </div>
    );
}
